import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

const DetailItem = ({ icon, label, value }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={1}
    alignItems={{ xs: "flex-start", sm: "center" }}
    sx={{ py: 0.2 }}
  >
    <Avatar sx={{ width: 22, height: 22 }}>
      {React.cloneElement(icon, {
        sx: { fontSize: 14, color: "primary.main" },
      })}
    </Avatar>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", lineHeight: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: "anywhere" }}>
        {value ?? "—"}
      </Typography>
    </Box>
  </Stack>
);

const getProjectLabel = (team) =>
  team?.projectName || (team?.projectId ? `Project ${team.projectId}` : "—");

const getClientLabel = (team) =>
  team?.clientName || (team?.clientId ? `Client ${team.clientId}` : "—");

const TeamDetails = ({
  team,
  mode = "view",
  clients = [],
  projectOptions = [],
  editForm,
  fieldSx,
  selectMenuProps,
  updating = false,
  onEditFormChange,
  onUpdateTeam,
}) => {
  const isEditing = mode === "edit";
  const selectedClientExists = clients.some(
    (client) => String(client.clientId) === String(editForm?.clientId),
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: "rgba(255,255,255,0.05)",
        color: "white",
      }}
    >
      <Stack alignItems="center" spacing={0.8} mb={1.5}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "primary.main",
            fontSize: 20,
          }}
        >
          {team?.teamName?.charAt(0) || <GroupsOutlinedIcon />}
        </Avatar>

        <Box textAlign="center">
          <Typography variant="subtitle2" fontWeight={600}>
            {team?.teamName || "Team"}
          </Typography>

        </Box>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {isEditing ? (
        <Stack component="form" spacing={2} onSubmit={onUpdateTeam}>
          <FormControl fullWidth sx={fieldSx} disabled>
            <InputLabel id="edit-team-client-label">Client</InputLabel>
            <Select
              labelId="edit-team-client-label"
              value={editForm?.clientId ?? ""}
              label="Client"
              MenuProps={selectMenuProps}
            >
              {editForm?.clientId && !selectedClientExists && (
                <MenuItem value={editForm.clientId}>
                  {getClientLabel(team)}
                </MenuItem>
              )}
              {clients.map((client) => (
                <MenuItem key={client.clientId} value={client.clientId}>
                  {client.clientName || `Client ${client.clientId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel id="edit-team-project-label">Project</InputLabel>
            <Select
              labelId="edit-team-project-label"
              value={editForm?.projectId ?? ""}
              label="Project"
              onChange={(event) =>
                onEditFormChange?.("projectId", event.target.value)
              }
              MenuProps={selectMenuProps}
            >
              {projectOptions.map((project) => (
                <MenuItem key={project.projectId} value={project.projectId}>
                  {project.projectName || `Project ${project.projectId}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Team Name"
            value={editForm?.teamName ?? ""}
            onChange={(event) =>
              onEditFormChange?.("teamName", event.target.value)
            }
            placeholder="Enter team name"
            sx={fieldSx}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={
              updating ||
              !editForm?.projectId ||
              !String(editForm?.teamName ?? "").trim()
            }
            sx={{
              width: { xs: "100%", sm: 160 },
              maxWidth: 240,
              alignSelf: "center",
              textTransform: "none",
              borderRadius: 2,
              background: "#4f8a3d",
              "&:hover": {
                background: "#3f6f31",
              },
            }}
          >
            {updating ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Update Team"
            )}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.2}>
          <DetailItem
            icon={<GroupsOutlinedIcon />}
            label="Team Name"
            value={team?.teamName}
          />
          <DetailItem
            icon={<BusinessOutlinedIcon />}
            label="Client"
            value={getClientLabel(team)}
          />
          <DetailItem
            icon={<FolderOutlinedIcon />}
            label="Project"
            value={getProjectLabel(team)}
          />
        </Stack>
      )}
    </Paper>
  );
};

export default TeamDetails;